import { useEffect, useState } from 'react';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import { AxiosError } from 'axios';

import {
  Subgroup,
  useCreateSubgroup,
  useUpdateSubgroup
} from '../../api/useSubgroups';

/**
 * Create (subgroup: null) or rename/recolor (subgroup set) a subgroup within
 * the active group. A 409 (duplicate name per group) is shown inline; the
 * global MutationCache.onError backstop still records the failure. Config
 * editing lives in `SubgroupConfigDialog` — this dialog only does identity.
 */

interface Props {
  open: boolean;
  groupId: string;
  subgroup: Subgroup | null;
  onClose: () => void;
}

export function CreateSubgroupDialog({
  open,
  groupId,
  subgroup,
  onClose
}: Props) {
  const createMutation = useCreateSubgroup();
  const updateMutation = useUpdateSubgroup();

  const [name, setName] = useState('');
  const [color, setColor] = useState('');
  const [sortOrder, setSortOrder] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setName(subgroup?.name ?? '');
    setColor(subgroup?.color ?? '');
    setSortOrder(subgroup?.sortOrder != null ? String(subgroup.sortOrder) : '');
    setError(null);
  }, [open, subgroup]);

  const busy = createMutation.isLoading || updateMutation.isLoading;

  const handleSubmit = async () => {
    setError(null);
    const trimmed = name.trim();
    if (!trimmed) {
      setError('Name is required.');
      return;
    }
    const sortNum = sortOrder.trim() ? Number(sortOrder) : undefined;
    try {
      if (subgroup) {
        await updateMutation.mutateAsync({
          id: subgroup._id,
          groupId,
          dto: {
            name: trimmed,
            color: color.trim() || undefined,
            sortOrder: sortNum
          }
        });
      } else {
        await createMutation.mutateAsync({
          groupId,
          name: trimmed,
          color: color.trim() || undefined,
          sortOrder: sortNum
        });
      }
      onClose();
    } catch (e) {
      const status = (e as AxiosError)?.response?.status;
      setError(
        status === 409
          ? 'A subgroup with this name already exists in this group.'
          : 'Failed to save the subgroup. Retry.'
      );
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontSize: '1rem' }}>
        {subgroup ? 'Edit subgroup' : 'New subgroup'}
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={1.5} sx={{ mt: 0.5 }}>
          <TextField
            size="small"
            label="Name"
            value={name}
            onChange={e => setName(e.target.value)}
            autoFocus
          />
          <TextField
            size="small"
            label="Color (hex, optional)"
            value={color}
            onChange={e => setColor(e.target.value)}
            placeholder="#42a5f5"
          />
          <TextField
            size="small"
            type="number"
            label="Sort order (optional)"
            value={sortOrder}
            onChange={e => setSortOrder(e.target.value)}
          />
          {error && (
            <Typography sx={{ fontSize: '0.78rem', color: 'error.main' }}>
              {error}
            </Typography>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={() => void handleSubmit()}
          disabled={busy}
        >
          {busy ? <CircularProgress size={14} color="inherit" /> : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
